import {TTableColumnFilterExpressionDto} from 'components/@common/widgets/custom-table/table-column-filter/table-column-filter-types';
import {ILookupEntry} from 'shared/types/lookups';
import {
    ERequestType,
    IFetchDataByIdArgsNumberType,
    TRequestTableResponseWithPagination,
} from 'shared/types/requests';
import {performRequest, showMessage} from 'shared/utils';
import {
    TDescriptiveFlexFieldData,
    TDescriptiveFlexfieldSegmentMinimizedEntity,
} from 'store/slices/descriptive-flexfields-slice/descriptive-flexfields-slice-types';

import {
    EAisArchiveSystemCode,
    IAisDocRegisterRawField,
    IAisInterfaceDocumentDto,
    TAisRegisterDocumentByTaxDto,
    IAisRegisterDocumentDto,
    IAisRegisterTax,
    IAisRequestDto,
    IAisRequestToPublishDto,
    IAisRequirementDto,
} from './ais-slice-types';

export type TFetchAisDocumentsRegisterParams = {
    dateFrom?: string;
    dateTo?: string;
    filters?: TTableColumnFilterExpressionDto;
    tableParamDto?: {
      paginationCurrent?: number;
      paginationPageSize?: number;
    };
};

export type TFetchAisDocumentsByTaxRegisterParams = {
    organizationId: number;
    dateFrom?: string;
    dateTo?: string;
    paginationCurrent?: number;
    paginationPageSize?: number;
};

export type TFetchAisDocumentsInterfaceDataParams = {
    filter: {
      counterpartyCountryCode?: string;
      counterpartyForeignRegNumber?: string;
      counterpartyInn?: string;
      counterpartyKpp?: string;
      counterpartyName?: string;
      dateFrom?: string;
      dateTo?: string;
      docJournalId?: number;
      documentBaseCode?: string;
      documentBaseDate?: string;
      documentBaseName?: string;
      documentBaseNumber?: string;
      documentCatCode?: string;
      documentCode?: string;
      documentDate?: string;
      documentName?: string;
      documentNumber?: string;
      documentSumGross?: number;
      documentTaxSum?: number;
      id?: number;
      loadId?: number;
      statusCode?: string;
    };
    pageRequestDto: {
      length?: number;
      paginationCurrent?: number;
      paginationPageSize?: number;
      search?: string;
      sorterField?: string;
      sorterOrder?: string;
      start?: string;
    };
  };

// ===== AIS-3 Requests =====

export type TFetchAisRequestsParams = {
    aisDocId?: IAisRegisterDocumentDto['aisDocId'];
};

export const fetchAisRequests = (args?: TFetchAisRequestsParams) => (
    performRequest<IAisRequestDto[]>({
        url: `integration.module/register.documents/requests/${args?.aisDocId}`,
        method: ERequestType.GET,
    })
);

// ===== Additional Placement Requests =====

export type TFetchAisRequestsToPublishParams = {
    id?: IAisRegisterDocumentDto['id'];
};

export const fetchAisRequestsToPublish = (args?: TFetchAisRequestsToPublishParams) => (
    performRequest<IAisRequestToPublishDto[]>({
        url: `integration.module/register.documents/requestsToPublish/${args?.id}`,
        method: ERequestType.GET,
    })
);

// ===== Requirements =====

export type TFetchAisRequirementsParams = {
    aisDocId?: IAisRegisterDocumentDto['aisDocId'];
};

export const fetchAisRequirements = (args?: TFetchAisRequirementsParams) => (
    performRequest<IAisRequirementDto[]>({
        url: `integration.module/register.documents/requirement/${args?.aisDocId}`,
        method: ERequestType.GET,
    })
);

// ===== Documents =====

export type TDeletePUDDocumentsArgs = {ids: string[]}
export const deletePUDDocuments = (args: TDeletePUDDocumentsArgs) => performRequest<
string>({
    url: 'integration.module/register.documents/file/del',
    method: ERequestType.POST,
    data: args.ids,
});

export interface IPostPUDFileToDocumentArgs {
    requestData: {
        aisDocId: string;
        archiveSystem: EAisArchiveSystemCode;
        file: File;
    };
    handleProgress?: (event: ProgressEvent) => void;
}

export const postPUDFileToDocument = (
    {
        requestData,
        handleProgress,
    }: IPostPUDFileToDocumentArgs,
) => {
    const {
        aisDocId,
        archiveSystem,
        file,
    } = requestData;

    const requestFormData = new FormData();

    if (aisDocId) requestFormData.append('aisDocId', aisDocId);
    if (archiveSystem) requestFormData.append('archiveSystem', archiveSystem);
    if (file) requestFormData.append('file', file);

    return performRequest({
        url: `integration.module/register.documents/file/${aisDocId}`,
        method: 'POST',
        headers: {'Content-Type': 'multipart/form-data'},
        data: requestFormData,
        onUploadProgress: progressEvent => {
            handleProgress?.(progressEvent);
        },
    });
};

export const fetchAisDocumentsRegister = (data?: TFetchAisDocumentsRegisterParams) => (
    performRequest<TRequestTableResponseWithPagination<IAisRegisterDocumentDto>>({
        url: 'integration.module/register.documents/documents',
        method: ERequestType.POST,
        data,
    })
);

export const transferAisDocumentsRegister = async () => {
    try {
        const response = await performRequest({
            url: 'integration.module/register.documents/transfer',
            method: ERequestType.GET,
        });
        if (response.status === 200) {
            showMessage({
                message: 'Документы были успешно перенесены в Реестр документов',
            });
        }
    } catch (e) {
        showMessage({
            message: e.message,
            isError: true,
        });
    }
};

export type TPutAisDocumentInRegisterArgs = Partial<IAisRegisterDocumentDto> & {
    id: IAisRegisterDocumentDto['id'];
}

export const putAisDocumentInRegister = (args: TPutAisDocumentInRegisterArgs) => {
    const {id, ...restArgs} = args;

    const requestFormData = new FormData();
    Object.keys(restArgs).forEach(k => {
        const v = (restArgs as {[index: string]: any})[k];
        if (v) requestFormData.set(k, v);
    });

    return performRequest<IAisRegisterDocumentDto>({
        url: `integration.module/register.documents/documents/${id}`,
        method: ERequestType.PUT,
        headers: {'Content-Type': 'multipart/form-data'},
        data: requestFormData,
    });
};

export type TPostAisDocumentInRegisterArgs = Omit<IAisRegisterDocumentDto, 'id'> & {}
export const postAisDocumentInRegister = (args: TPostAisDocumentInRegisterArgs) => {
    const requestFormData = new FormData();
    Object.keys(args).forEach(k => {
        const v = (args as {[index: string]: any})[k];
        if (v) requestFormData.set(k, v);
    });

    return performRequest<IAisRegisterDocumentDto>({
        url: 'integration.module/register.documents/documents/',
        method: ERequestType.POST,
        headers: {'Content-Type': 'multipart/form-data'},
        data: requestFormData,
    });
};

export type TDeleteAisDocumentsInRegisterArgs = {ids: number[]}
export const deleteAisDocumentsInRegister = (args: TDeleteAisDocumentsInRegisterArgs) => performRequest<
string>({
    url: 'integration.module/register.documents/documents/del',
    method: ERequestType.POST,
    data: args,
});

export type TPostUpdateAisDocumentsActiveFlag = {ids: number[]}
export const postUpdateAisDocumentsActiveFlag = (args: TPostUpdateAisDocumentsActiveFlag) => performRequest<
string>({
    url: 'integration.module/register.documents/update-active-flag',
    method: ERequestType.POST,
    data: args,
});

type TAisDocsSettingsAttributeKey = (
    'use_parameters'
    | 'quantity_lines'
    | 'content_availability_condition'
    | 'ais_doc_id_autogeneration'
);

interface IAisDocsSettingAttribute {
    id: string;
    attribute: TAisDocsSettingsAttributeKey;
    value: string;
}

export type TAisDocumentsRegisterSettings = IAisDocsSettingAttribute[];
export const fetchAisDocumentsRegisterSettings = () => (
    performRequest<TAisDocumentsRegisterSettings>({
        url: 'integration.module/register.documents/param-settings',
        method: ERequestType.GET,
    })
);

export type TAisDocRegisterFieldsRaw = IAisDocRegisterRawField[];
export const fetchAisDocumentsRegisterFields = () => (
    performRequest<TAisDocRegisterFieldsRaw>({
        url: 'integration.module/register.documents/form-settings',
        method: ERequestType.GET,
    })
);

// ===== Documents By Tax =====

export const fetchAisDocumentsByTaxRegister = (args?: TFetchAisDocumentsByTaxRegisterParams) => (
    performRequest<TRequestTableResponseWithPagination<TAisRegisterDocumentByTaxDto>>({
        url: 'integration.module/register.documents/documents-by-tax',
        method: ERequestType.GET,
        params: args,
    })
);

// =====  Included Documents By Tax =====

export type TFetchAisIncludedDocumentsByTaxArgs = {
    id: number;
}
export const fetchAisIncludedDocumentsByTax = (
    args: TFetchAisIncludedDocumentsByTaxArgs,
) => performRequest<TAisRegisterDocumentByTaxDto[]>(
    {
        url: `integration.module/register.documents/documents-by-tax/tbd/${args.id}`,
        method: ERequestType.GET,
    },
);

export type TPostAisDocumentByTaxArgs = Partial<Omit<IAisRegisterTax, 'id'>> & {
    id: IAisRegisterDocumentDto['id']; // base document id
} & Required<Pick<IAisRegisterTax, 'kndCode' | 'reportingYear' | 'taxCode' | 'corrNum' | 'taxPeriod'>>

export const postAisDocumentByTax = (args: TPostAisDocumentByTaxArgs) => {
    const requestFormData = new FormData();
    Object.keys(args).forEach(k => {
        const v = (args as {[index: string]: any})[k];
        if (v) requestFormData.set(k, v);
    });

    return performRequest<TAisRegisterDocumentByTaxDto>({
        url: 'integration.module/register.documents/documents-by-tax/',
        method: ERequestType.POST,
        headers: {'Content-Type': 'multipart/form-data'},
        data: requestFormData,
    });
};

export type TPutAisDocumentByTaxArgs = Partial<Omit<IAisRegisterTax, 'id' | 'mappingId'>> & {
    id: IAisRegisterDocumentDto['id']; // base
} & Required<Pick<IAisRegisterTax, 'kndCode' | 'reportingYear' | 'taxCode' | 'corrNum' | 'taxPeriod' | 'mappingId'>>

export const putAisDocumentByTax = (args: TPutAisDocumentByTaxArgs) => {
    const {mappingId, ...restArgs} = args;

    const requestFormData = new FormData();
    Object.keys(restArgs).forEach(k => {
        const v = (restArgs as {[index: string]: any})[k];
        if (v) requestFormData.set(k, v);
    });

    return performRequest<TAisRegisterDocumentByTaxDto>({
        url: `integration.module/register.documents/documents-by-tax/${mappingId}`,
        method: ERequestType.PUT,
        headers: {'Content-Type': 'multipart/form-data'},
        data: requestFormData,
    });
};

export type TDeleteAisDocumentsByTaxArgs = {ids: number[]}
export const deleteAisDocumentsByTax = (args: TDeleteAisDocumentsInRegisterArgs) => performRequest<
string>({
    url: 'integration.module/register.documents/documents-by-tax/del',
    method: ERequestType.POST,
    data: args,
});

// ===== Documents Register AdditionalFields =====

export const fetchAisDocAdditionalFieldsByDocId = (
    args: IFetchDataByIdArgsNumberType,
) => performRequest<TDescriptiveFlexFieldData>(
    {
        url: `integration.module/register.documents/get-flex-field/${args.id}`,
        method: ERequestType.GET,
    },
);

export type TPostUpdateAisDocumentsAdditionalFields = {
    id?: number;
    values: TDescriptiveFlexfieldSegmentMinimizedEntity;
}
export const postUpdateAisDocumentsAdditionalFields = (args: TPostUpdateAisDocumentsAdditionalFields) => performRequest<
string>({
    url: `integration.module/register.documents/update-flex-field/${args.id}`,
    method: ERequestType.POST,
    data: args?.values,
});

// =====  Response requirement =====
export type TFormTransportContainerProtocolRequestArgs = {
    containerId: number;
    signatoryId: number;
}

export const formTransportContainerProtocolRequest = (
    args: TFormTransportContainerProtocolRequestArgs,
) => performRequest<string>(
    {
        url: 'integration.module/response.requirement/generateProtocolErrorMessage',
        method: ERequestType.POST,
        data: args,
    },
);

// ===== Documents Interface =====

export type TAisDocInterfaceFieldsRaw = IAisDocRegisterRawField[];
export const fetchAisDocumentsInterfaceFields = () => (
    performRequest<TAisDocInterfaceFieldsRaw>({
        url: 'integration.module/doc-journal-interface/get-form-settings',
        method: ERequestType.GET,
    })
);

export const fetchAisDocumentsInterfaceData = (args?: TFetchAisDocumentsInterfaceDataParams) => (
    performRequest<TRequestTableResponseWithPagination<IAisInterfaceDocumentDto>>({
        url: 'integration.module/doc-journal-interface/data',
        method: ERequestType.POST,
        data: args,
    })
);

export type TDeleteAisDocumentsInInterfaceArgs = {ids: number[]}
export const deleteAisDocumentsInInterface = (args: TDeleteAisDocumentsInInterfaceArgs) => performRequest<
string>({
    url: 'integration.module/doc-journal-interface/update-status/delete',
    method: ERequestType.POST,
    data: args,
});

export type TDeleteAisDocumentsInInterfaceByFilterArgs = Partial<IAisInterfaceDocumentDto>
export const deleteAisDocumentsInInterfaceByFilter = (args: TDeleteAisDocumentsInInterfaceByFilterArgs) => (
    performRequest<string>({
        url: 'integration.module/doc-journal-interface/update-status/by-filter/delete',
        method: ERequestType.POST,
        data: args,
    }));

export type TUpdateStatusAisDocumentsInInterfaceArgs = {ids: number[]}
export const updateStatusAisDocumentsInInterface = (args: TUpdateStatusAisDocumentsInInterfaceArgs) => (
    performRequest<string>({
        url: 'integration.module/doc-journal-interface/update-status/change',
        method: ERequestType.POST,
        data: args,
    }));

export type TUpdateStatusAisDocumentsInInterfaceByFilterArgs = Partial<IAisInterfaceDocumentDto>
export const updateStatusAisDocumentsInInterfaceByFilter = (args: TUpdateStatusAisDocumentsInInterfaceByFilterArgs) => (
    performRequest<string>({
        url: 'integration.module/doc-journal-interface/update-status/by-filter/change',
        method: ERequestType.POST,
        data: args,
    }));

export type TPublishAisInterfaceDocumentsArgs = {orgId: number}
export const publishAisInterfaceDocuments = ({orgId}: TPublishAisInterfaceDocumentsArgs) => performRequest<string>({
    url: `integration.module/doc-journal-interface/publish-all?orgId=${orgId}`,
    method: ERequestType.POST,
});

export type TUpdateAisDocumentInterfaceCardArgs = Partial<IAisInterfaceDocumentDto>;
export const updateAisDocumentInterfaceCard = (args: TUpdateAisDocumentInterfaceCardArgs) => performRequest<
    IAisInterfaceDocumentDto>({
        url: 'integration.module/doc-journal-interface/edit',
        method: ERequestType.POST,
        data: args,
    });

export const fetchAisDocumentsRegisterFilterConditions = () => performRequest<ILookupEntry[]>({
    url: 'lookupValue/TABLE_REPORT_FILTER_VIEW',
    method: 'GET',
});
